import { useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Search, Plus, Mail, Users, FileText, History } from 'lucide-react'
import { LoadingSpinner } from '@/components/common/feedback/LoadingSpinner'
import { useDebouncedValue } from '@/lib/hooks/useDebouncedValue'
import { useArtists, useSendEmailToArtist } from '../hooks/useArtistAdmin'
import {
  useEmailCampaigns,
  useEmailTemplates,
  useEmailHistory,
  useEmailStats,
  useSendBulkEmail,
  useCreateEmailTemplate,
  useUpdateEmailTemplate,
  useDeleteEmailTemplate,
  useCreateEmailCampaign,
  useUpdateEmailCampaign,
  useDeleteEmailCampaign,
  useSendEmailCampaign,
} from '../hooks/useEmailAdmin'
import { ArtistTable } from '../components/ArtistTable'
import { SendEmailDialog } from '../components/SendEmailDialog'
import { CampaignTable } from '../components/CampaignTable'
import { TemplateTable } from '../components/TemplateTable'
import { TemplateEditorDialog, type TemplateFormData } from '../components/TemplateEditorDialog'
import { CommunicationHistory } from '../components/CommunicationHistory'
import { EmailStatsCard } from '../components/EmailStatsCard'
import type { ArtistWithDetails, EmailTemplate, EmailCampaign } from '../types'

export function ArtistAdminPage() {
  const [activeTab, setActiveTab] = useState('artists')
  const [search, setSearch] = useState('')
  const [verifiedFilter, setVerifiedFilter] = useState<string>('all')

  // Debounce search for better performance
  const debouncedSearch = useDebouncedValue(search, 300)

  // Artist state
  const [selectedArtistIds, setSelectedArtistIds] = useState<number[]>([])
  const [emailDialogOpen, setEmailDialogOpen] = useState(false)
  const [emailTarget, setEmailTarget] = useState<ArtistWithDetails | null>(null)
  const [bulkEmailDialogOpen, setBulkEmailDialogOpen] = useState(false)

  // Template state
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null)

  // Campaign state
  const [, setCampaignDialogOpen] = useState(false)
  const [, setEditingCampaign] = useState<EmailCampaign | null>(null)

  // Queries - use debounced search, show 10 recent by default or 50 when searching
  const { data: artists = [], isLoading: artistsLoading } = useArtists({
    search: debouncedSearch || undefined,
    limit: debouncedSearch ? 50 : 10,
  })
  const { data: campaignsData, isLoading: campaignsLoading } = useEmailCampaigns()
  const { data: templatesData, isLoading: templatesLoading } = useEmailTemplates()
  const { data: historyData, isLoading: historyLoading } = useEmailHistory({ limit: 100 })
  const { data: emailStats } = useEmailStats()

  // Mutations
  const sendEmailToArtist = useSendEmailToArtist()
  const sendBulkEmail = useSendBulkEmail()
  const createTemplate = useCreateEmailTemplate()
  const updateTemplate = useUpdateEmailTemplate()
  const deleteTemplate = useDeleteEmailTemplate()
  useCreateEmailCampaign()
  useUpdateEmailCampaign()
  const deleteCampaign = useDeleteEmailCampaign()
  const sendCampaign = useSendEmailCampaign()

  // Handlers
  const handleViewArtist = (artist: ArtistWithDetails) => {
    // Navigate to artist detail page - for now just show in a new tab
    window.open(`/app/artists/${artist.id}`, '_blank')
  }

  const handleSendEmailToArtist = (artist: ArtistWithDetails) => {
    setEmailTarget(artist)
    setEmailDialogOpen(true)
  }

  const handleSendEmail = async (data: { subject: string; htmlContent: string; textContent: string }) => {
    if (emailTarget) {
      await sendEmailToArtist.mutateAsync({
        artistId: emailTarget.id,
        payload: data,
      })
    }
  }

  const handleBulkEmail = async (data: { subject: string; htmlContent: string; textContent: string }) => {
    await sendBulkEmail.mutateAsync({
      artistIds: selectedArtistIds,
      ...data,
    })
    setSelectedArtistIds([])
  }

  const handleSaveTemplate = async (data: TemplateFormData) => {
    if (editingTemplate) {
      await updateTemplate.mutateAsync({
        templateId: editingTemplate.id,
        payload: data,
      })
    } else {
      await createTemplate.mutateAsync(data)
    }
    setEditingTemplate(null)
  }

  const handleDeleteTemplate = async (template: EmailTemplate) => {
    if (confirm(`Are you sure you want to delete "${template.name}"?`)) {
      await deleteTemplate.mutateAsync(template.id)
    }
  }

  const handleSendCampaign = async (campaign: EmailCampaign) => {
    if (confirm(`Send "${campaign.name}" to ${campaign.recipientCount} recipients?`)) {
      await sendCampaign.mutateAsync(campaign.id)
    }
  }

  const handleDeleteCampaign = async (campaign: EmailCampaign) => {
    if (confirm(`Are you sure you want to delete "${campaign.name}"?`)) {
      await deleteCampaign.mutateAsync(campaign.id)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-serif font-semibold">Artist Communications</h1>
          <p className="text-muted-foreground">
            Manage artists, email templates, campaigns, and communication history
          </p>
        </div>
      </div>

      {emailStats && <EmailStatsCard stats={emailStats} title="Overall Email Performance" />}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="artists" className="gap-2">
            <Users className="h-4 w-4" />
            Artists
          </TabsTrigger>
          <TabsTrigger value="campaigns" className="gap-2">
            <Mail className="h-4 w-4" />
            Campaigns
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-2">
            <FileText className="h-4 w-4" />
            Templates
          </TabsTrigger>
          <TabsTrigger value="history" className="gap-2">
            <History className="h-4 w-4" />
            History
          </TabsTrigger>
        </TabsList>

        {/* Artists Tab */}
        <TabsContent value="artists" className="space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>All Artists</CardTitle>
                  <CardDescription>
                    {artists.length} artists total
                    {selectedArtistIds.length > 0 && ` (${selectedArtistIds.length} selected)`}
                  </CardDescription>
                </div>
                {selectedArtistIds.length > 0 && (
                  <Button onClick={() => setBulkEmailDialogOpen(true)}>
                    <Mail className="mr-2 h-4 w-4" />
                    Email Selected ({selectedArtistIds.length})
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search artists..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={verifiedFilter} onValueChange={setVerifiedFilter}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="verified">Verified</SelectItem>
                    <SelectItem value="unverified">Unverified</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {artistsLoading ? (
                <LoadingSpinner />
              ) : (
                <ArtistTable
                  artists={artists}
                  selectedIds={selectedArtistIds}
                  onSelectionChange={setSelectedArtistIds}
                  onViewArtist={handleViewArtist}
                  onSendEmail={handleSendEmailToArtist}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Campaigns Tab */}
        <TabsContent value="campaigns" className="space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Email Campaigns</CardTitle>
                  <CardDescription>Create and manage bulk email campaigns</CardDescription>
                </div>
                <Button onClick={() => { setEditingCampaign(null); setCampaignDialogOpen(true) }}>
                  <Plus className="mr-2 h-4 w-4" />
                  New Campaign
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {campaignsLoading ? (
                <LoadingSpinner />
              ) : (
                <CampaignTable
                  campaigns={campaignsData?.data || []}
                  onView={(c) => window.open(`/app/artists/campaigns/${c.id}`, '_blank')}
                  onEdit={(c) => { setEditingCampaign(c); setCampaignDialogOpen(true) }}
                  onDelete={handleDeleteCampaign}
                  onSend={handleSendCampaign}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Email Templates</CardTitle>
                  <CardDescription>Manage reusable email templates</CardDescription>
                </div>
                <Button onClick={() => { setEditingTemplate(null); setTemplateDialogOpen(true) }}>
                  <Plus className="mr-2 h-4 w-4" />
                  New Template
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {templatesLoading ? (
                <LoadingSpinner />
              ) : (
                <TemplateTable
                  templates={templatesData?.data || []}
                  onView={(t) => { setEditingTemplate(t); setTemplateDialogOpen(true) }}
                  onEdit={(t) => { setEditingTemplate(t); setTemplateDialogOpen(true) }}
                  onDelete={handleDeleteTemplate}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Communication History</CardTitle>
              <CardDescription>Recent email sends across all artists</CardDescription>
            </CardHeader>
            <CardContent>
              {historyLoading ? (
                <LoadingSpinner />
              ) : (
                <CommunicationHistory emails={historyData?.data || []} />
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <SendEmailDialog
        open={emailDialogOpen}
        onOpenChange={setEmailDialogOpen}
        recipientName={emailTarget ? `${emailTarget.firstName} ${emailTarget.lastName}` : ''}
        onSend={handleSendEmail}
        isLoading={sendEmailToArtist.isPending}
      />

      <SendEmailDialog
        open={bulkEmailDialogOpen}
        onOpenChange={setBulkEmailDialogOpen}
        recipientName=""
        recipientCount={selectedArtistIds.length}
        isBulk
        onSend={handleBulkEmail}
        isLoading={sendBulkEmail.isPending}
      />

      <TemplateEditorDialog
        open={templateDialogOpen}
        onOpenChange={setTemplateDialogOpen}
        template={editingTemplate}
        onSave={handleSaveTemplate}
        isLoading={createTemplate.isPending || updateTemplate.isPending}
      />
    </div>
  )
}
